import { Fragment, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IOrganizationFeeTemplate } from "../../../../setup/api/services/organization/models";
import { BNPL_PROVIDERS, TOrganizationPaymentMethods } from "../api";
import { getBrandFeeLabel, IFeeEstimate } from "../fees";
import { TwoPartyContractInputs } from "../schema";
import Amount from "../amount";
import FloatingField from "../ui/FloatingField";
import CheckRow from "../ui/CheckRow";
import styles from "../styles.module.scss";

/** "50000.5" → "50,000.5" while keeping what is being typed (a trailing "." stays). */
const formatTyped = (raw: string) => {
  const [whole, fraction] = raw.split(".");
  const grouped = whole ? Number(whole).toLocaleString("en-US") : "";
  return fraction !== undefined ? `${grouped || "0"}.${fraction}` : grouped;
};

/** Digits and one decimal point, two decimals at most. */
const cleanTyped = (value: string) => {
  const [whole = "", ...rest] = value.replace(/[^\d.]/g, "").split(".");
  const fraction = rest.join("").slice(0, 2);
  return rest.length ? `${whole}.${fraction}` : whole;
};

interface IAmountFieldProps {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur: () => void;
  label: string;
  currency: string;
  errorMessage?: string;
}

const AmountField = ({
  value,
  onChange,
  onBlur,
  label,
  currency,
  errorMessage,
}: IAmountFieldProps) => {
  // The typed text is kept as typed ("12." while entering decimals); the form holds the number.
  const [typed, setTyped] = useState(value == null ? "" : `${value}`);
  return (
    <FloatingField
      rounded
      inputMode="decimal"
      label={label}
      value={formatTyped(typed)}
      adornment={currency}
      errorMessage={errorMessage}
      onBlur={onBlur}
      onChange={(event) => {
        const next = cleanTyped(event.target.value);
        setTyped(next);
        onChange(next === "" || next === "." ? null : Number(next));
      }}
    />
  );
};

interface IStepValueProps {
  organizationMethods: TOrganizationPaymentMethods;
  feeTemplate?: IOrganizationFeeTemplate;
  estimate?: IFeeEstimate;
}

/** Step three: the value the buyer pays, installment providers, and the seller's net. */
const StepValue = ({
  organizationMethods,
  feeTemplate,
  estimate,
}: IStepValueProps) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = useFormContext<TwoPartyContractInputs>();
  const amount = useWatch({ control, name: "amount" }) || 0;

  // Only the installment providers the organization is enabled for.
  const providers = BNPL_PROVIDERS.filter(
    (provider) => !!organizationMethods[provider.type]?.length
  );

  return (
    <div className={styles.fieldsLg}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{t("lbl_transaction_value")}</h3>
          <p className={styles.sectionSubtitle}>
            {t("lbl_transaction_value_hint")}
          </p>
        </div>
        <Controller
          name="amount"
          control={control}
          render={({ field }) => (
            <AmountField
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              label={t("lbl_value")}
              currency={t("lbl_saudi_currency")}
              errorMessage={errors?.amount?.message?.toString()}
            />
          )}
        />
      </div>

      {providers.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeaderLoose}>
            <h3 className={styles.sectionLabel}>
              {t("lbl_allow_installments")}
            </h3>
            <p className={styles.sectionSubtitle}>
              {t("lbl_allow_installments_hint")}
            </p>
          </div>
          <div className={styles.optionsBox}>
            {providers.map((provider, index) => {
              const fee = getBrandFeeLabel(
                feeTemplate,
                amount,
                provider.brand,
                t("lbl_saudi_currency")
              );
              return (
                <Fragment key={provider.key}>
                  {index > 0 && <div className={styles.optionDivider} />}
                  <Controller
                    name={`installments.${provider.key}`}
                    control={control}
                    render={({ field }) => (
                      <CheckRow
                        checked={!!field.value}
                        onChange={field.onChange}
                        label={
                          fee
                            ? t("lbl_installment_provider_fee", {
                                provider: t(provider.labelKey),
                                fee,
                              })
                            : t(provider.labelKey)
                        }
                      />
                    )}
                  />
                </Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionHeaderLoose}>
          <h3 className={styles.sectionLabel}>{t("lbl_fees_summary")}</h3>
          <p className={styles.sectionSubtitle}>{t("lbl_fees_summary_hint")}</p>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryRow}>
            <div className={styles.summaryLabel}>
              <p className={styles.summaryMuted}>{t("lbl_waffy_fees")}</p>
              <p className={styles.summaryHint}>
                {t("lbl_waffy_fees_deducted_hint")}
              </p>
            </div>
            <Amount value={estimate?.fee} />
          </div>
          <div className={styles.summaryDivider} />
          <div className={styles.summaryRow}>
            <p className={styles.summaryStrong}>{t("lbl_net_you_receive")}</p>
            <Amount value={estimate?.net} highlighted />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepValue;
